import { useEffect } from 'react';
import { SEO } from '../../utils/seo';
import { Link } from 'react-router-dom';
import Footer from '../../components/feature/Footer';

export default function TermsOfServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Terms of Service | TRex RE LLC El Paso"
        description="Terms of Service for TRex RE LLC. Review the terms and conditions for using our website and services."
        keywords="terms of service, terms and conditions, user agreement"
        canonical="/terms-of-service"
      />
      
      {/* Simple Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/" className="inline-flex items-center space-x-2 text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="text-sm font-medium">Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
        <p className="text-gray-500 mb-12">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

        <div className="prose prose-lg max-w-none">
          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Agreement to Terms</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Welcome to TRex RE LLC. By accessing or using our website and services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our website or services.
            </p>
            <p className="text-gray-600 leading-relaxed">
              These Terms constitute a legally binding agreement between you and TRex RE LLC ("Company," "we," "our," or "us") regarding your use of our website and services.
            </p>
          </section>

          {/* Services Description */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Description of Services</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              TRex RE LLC provides real estate investment services, including but not limited to:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
              <li>Purchasing residential and commercial properties for cash</li>
              <li>Providing property evaluations and cash offers</li>
              <li>Assisting homeowners facing foreclosure, title issues, or other property challenges</li>
              <li>Buying inherited properties, rental properties, and distressed properties</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-4">
              Our services are provided "as is" and we reserve the right to modify, suspend, or discontinue any aspect of our services at any time.
            </p>
          </section>

          {/* Use of Website */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Use of Website</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Permitted Use</h3>
                <p className="text-gray-600 leading-relaxed">
                  You may use our website for lawful purposes only. You agree not to use our website in any way that violates applicable laws or regulations.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Prohibited Activities</h3>
                <p className="text-gray-600 leading-relaxed mb-3">
                  You agree not to:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                  <li>Use our website for any fraudulent or unlawful purpose</li>
                  <li>Interfere with or disrupt the operation of our website</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Transmit viruses, malware, or other harmful code</li>
                  <li>Collect or harvest information about other users</li>
                  <li>Impersonate any person or entity</li>
                  <li>Submit false or misleading information</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Property Transactions */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Property Transactions</h2>
            <div className="space-y-4">
              <p className="text-gray-600 leading-relaxed">
                <strong>No Guarantee of Purchase:</strong> Submitting property information or receiving a cash offer does not guarantee that we will purchase your property. All transactions are subject to our evaluation, due diligence, and mutual agreement on terms.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>Accuracy of Information:</strong> You represent and warrant that all information you provide about your property is accurate, complete, and not misleading. You are responsible for disclosing all material facts about the property.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>Independent Legal Advice:</strong> We recommend that you consult with independent legal, financial, and tax advisors before entering into any real estate transaction with us.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>No Agency Relationship:</strong> Unless otherwise agreed in writing, we are not acting as your agent or representative. We are purchasing properties for our own account as a principal.
              </p>
            </div>
          </section>

          {/* Intellectual Property */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Intellectual Property Rights</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              All content on our website, including text, graphics, logos, images, and software, is the property of TRex RE LLC or its licensors and is protected by copyright, trademark, and other intellectual property laws.
            </p>
            <p className="text-gray-600 leading-relaxed">
              You may not reproduce, distribute, modify, or create derivative works from our content without our express written permission.
            </p>
          </section>

          {/* Disclaimers */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Disclaimers and Limitations of Liability</h2>
            <div className="space-y-4">
              <p className="text-gray-600 leading-relaxed">
                <strong>No Warranties:</strong> Our website and services are provided "as is" without warranties of any kind, either express or implied. We do not warrant that our website will be uninterrupted, error-free, or secure.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>No Professional Advice:</strong> Information on our website is for general informational purposes only and does not constitute legal, financial, or tax advice. You should consult with appropriate professionals for advice specific to your situation.
              </p>
              <p className="text-gray-600 leading-relaxed">
                <strong>Limitation of Liability:</strong> To the fullest extent permitted by law, TRex RE LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of our website or services.
              </p>
            </div>
          </section>

          {/* Indemnification */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Indemnification</h2>
            <p className="text-gray-600 leading-relaxed">
              You agree to indemnify, defend, and hold harmless TRex RE LLC and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising out of your use of our website, violation of these Terms, or infringement of any third-party rights.
            </p>
          </section>

          {/* Governing Law */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Governing Law and Dispute Resolution</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              These Terms shall be governed by and construed in accordance with the laws of the State of Texas, without regard to its conflict of law provisions.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Any disputes arising out of or relating to these Terms or our services shall be resolved through binding arbitration in El Paso County, Texas, in accordance with the rules of the American Arbitration Association.
            </p>
          </section>

          {/* Modifications */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Modifications to Terms</h2>
            <p className="text-gray-600 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify you of any material changes by posting the updated Terms on our website and updating the "Last updated" date. Your continued use of our website after such changes constitutes your acceptance of the modified Terms.
            </p>
          </section>

          {/* Severability */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Severability</h2>
            <p className="text-gray-600 leading-relaxed">
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.
            </p>
          </section>

          {/* Entire Agreement */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Entire Agreement</h2>
            <p className="text-gray-600 leading-relaxed">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and TRex RE LLC regarding your use of our website and services.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-gray-50 rounded-lg p-6 space-y-3">
              <p className="text-gray-900 font-semibold">TRex RE LLC</p>
              <p className="text-gray-600">3016 Gull Lake Pl, El Paso, TX 79936</p>
              <p className="text-gray-600">
                Phone: <a href="tel:+1-915-206-5814" className="text-teal-600 hover:text-teal-700 cursor-pointer">(915) 206-5814</a>
              </p>
              <p className="text-gray-600">
                Email: <a href="mailto:ElPaso@trexrealestate.com" className="text-teal-600 hover:text-teal-700 cursor-pointer">ElPaso@trexrealestate.com</a>
              </p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}